import { useEffect, useMemo, type FunctionComponent } from "react";
import { Alert, Form, Input, Select, Tag } from "antd";
import { type FormInstance } from "antd/lib/form";
import { useTranslation } from "react-i18next";

import { type Product } from "@fitness-app/data-models/entities/Product";
import {
  WFirmaIntegrationStatus,
  type FakturowniaIntegration,
  type IFirmaIntegration,
  type InFaktIntegration,
  type WFirmaIntegration,
} from "@fitness-app/data-models/entities/ProductsSettings";

import ImageUploadField from "~/components/ImageUploadField/ImageUploadField";
import { LIST_ITEM_SEPARATOR } from "~/constants/separators";
import { type DuplicateProductFormModel } from "~/modules/Products/hooks/useDuplicateProduct";
import { usePaymentGatewayOptions } from "~/modules/Products/ProductForm/hooks/usePaymentGatewayOptions";
import { useAppSelector } from "~/store/initializeStore";

interface OwnProps {
  formController?: FormInstance<DuplicateProductFormModel>;
  onSubmit: (formData: DuplicateProductFormModel) => void;
  source: Product;
  automationCounts?: { tasks: number; webhooks: number };
}

type Props = OwnProps;

const DuplicateProductForm: FunctionComponent<Props> = ({ formController, onSubmit, source, automationCounts }) => {
  const { t } = useTranslation(["common", "products"]);
  const userData = useAppSelector((store) => store.user.data);
  const integrationData = useAppSelector((store) => store.productsSettings.data);
  const invoices = integrationData?.invoices;

  const { tpayOptions, stripeOptions, stripeOptionsByKey, tpayOptionsByKey } = usePaymentGatewayOptions();

  const customStripeAccount = Form.useWatch("customStripeAccount", formController);
  const tpayIntegration = Form.useWatch("tpayIntegration", formController);

  const invoicesOptions = useMemo(() => {
    if (!invoices) {
      return [];
    }

    return Object.values(invoices)
      .map((invoiceClient) =>
        invoiceClient?.filter(
          (item) => !("status" in item) || ("status" in item && item.status === WFirmaIntegrationStatus.Completed),
        ),
      )
      .map((invoiceClient: (FakturowniaIntegration | IFirmaIntegration | WFirmaIntegration | InFaktIntegration)[]) =>
        invoiceClient?.map(
          (account: FakturowniaIntegration | IFirmaIntegration | WFirmaIntegration | InFaktIntegration) => ({
            label: `${account.accountName} - ${t(`invoices.${account.client}`)}`,
            value: `${account.accountName}${LIST_ITEM_SEPARATOR}${account.client}${LIST_ITEM_SEPARATOR}${account.url}`,
          }),
        ),
      )
      .filter((item): item is { label: string; value: string }[] => !!item)
      .flat();
  }, [invoices, t]);

  const sourceCustomInvoicesValue = useMemo(() => {
    if (!source.customInvoicesClient) {
      return null;
    }
    const { accountName, client, accountUrl } = source.customInvoicesClient;
    return `${accountName}${LIST_ITEM_SEPARATOR}${client}${LIST_ITEM_SEPARATOR}${accountUrl}`;
  }, [source.customInvoicesClient]);

  const sourceStripeSelectValue = useMemo(() => {
    if (!source.customStripeAccount) {
      return null;
    }
    const isDefault = stripeOptionsByKey[source.customStripeAccount]?.isDefault ?? false;
    return `${source.customStripeAccount}${LIST_ITEM_SEPARATOR}${isDefault}`;
  }, [source.customStripeAccount, stripeOptionsByKey]);

  const sourceTpaySelectValue = useMemo(() => {
    if (!source.tpayIntegration) {
      return null;
    }
    const isDefault = tpayOptionsByKey[source.tpayIntegration]?.isDefault ?? false;
    return `${source.tpayIntegration}${LIST_ITEM_SEPARATOR}${isDefault}`;
  }, [source.tpayIntegration, tpayOptionsByKey]);

  const initialValues: DuplicateProductFormModel = useMemo(
    () => ({
      sourceProductId: source.id,
      name: `${source.name} (kopia)`,
      description: source.description,
      images: source.images ?? [],
      customStripeAccount: sourceStripeSelectValue,
      tpayIntegration: sourceTpaySelectValue,
      customInvoicesClient: sourceCustomInvoicesValue,
    }),
    [source, sourceCustomInvoicesValue, sourceStripeSelectValue, sourceTpaySelectValue],
  );

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    formController?.setFieldsValue(initialValues as any);
  }, [initialValues, formController]);

  const selectedStripeAccountId = customStripeAccount?.split(LIST_ITEM_SEPARATOR)[0] ?? null;
  const selectedTpayPosId = tpayIntegration?.split(LIST_ITEM_SEPARATOR)[0] ?? null;

  const stripeAccountChanged = (selectedStripeAccountId ?? null) !== (source.customStripeAccount ?? null);
  const tpayChanged = (selectedTpayPosId ?? null) !== (source.tpayIntegration ?? null);
  const gatewayChanged = stripeAccountChanged || tpayChanged;

  const hasReferences =
    (source.upsellTo?.length ?? 0) > 0 ||
    (source.crossSell?.products?.length ?? 0) > 0 ||
    (source.endAccessOffers?.length ?? 0) > 0;

  return (
    <Form<DuplicateProductFormModel>
      name="duplicate-product-form"
      labelCol={{ span: 8 }}
      wrapperCol={{ span: 16 }}
      layout="horizontal"
      labelWrap
      form={formController}
      initialValues={initialValues}
      onFinish={onSubmit}
    >
      <Form.Item name="sourceProductId" hidden>
        <Input />
      </Form.Item>

      <Alert
        type="info"
        showIcon
        className="mb-6"
        message={t<string>("products:duplicate.summaryTitle", "Duplikujesz produkt")}
        description={
          <div className="flex flex-col gap-1">
            <span>
              {t<string>("products:duplicate.summarySource", "Źródło")}: <Tag>{source.name}</Tag>
            </span>
            {automationCounts ? (
              <span>
                {t<string>("products:duplicate.automationCopied", "Skopiowane zostaną")}:{" "}
                <Tag color="blue">{automationCounts.tasks} tasków</Tag>
                <Tag color="purple">{automationCounts.webhooks} webhooków</Tag>
              </span>
            ) : null}
          </div>
        }
      />

      <Form.Item
        name="name"
        label={t<string>("products:form.name")}
        rules={[{ required: true, message: t<string>("validationErrors.fieldIsRequired") }]}
      >
        <Input placeholder={t<string>("products:form.namePlaceholder")} style={{ maxWidth: 700 }} />
      </Form.Item>

      <Form.Item
        name="description"
        label={t<string>("products:form.description")}
        rules={[{ required: true, message: t<string>("validationErrors.fieldIsRequired") }]}
      >
        <Input.TextArea placeholder={t<string>("products:form.descPlaceholder")} style={{ maxWidth: 700 }} />
      </Form.Item>

      <Form.Item
        tooltip={t<string>("products:form.imageAdditionalInfo")}
        name="images"
        label={t<string>("products:form.images")}
        valuePropName="fileList"
        getValueFromEvent={(e: unknown) => e}
      >
        <ImageUploadField storageRef={`${userData?.id}/products`} multiple={false} />
      </Form.Item>

      <Form.Item
        name="customInvoicesClient"
        tooltip={t<string>("products:form.customInvoicesClientTooltip")}
        label={t<string>("products:form.customInvoicesClient")}
      >
        <Select
          allowClear
          options={invoicesOptions}
          placeholder={t<string>("products:form.customInvoicesClientPlaceholder")}
        />
      </Form.Item>

      <Form.Item
        name="tpayIntegration"
        tooltip={t<string>("products:form.customTpayClientTooltip")}
        label={t<string>("products:form.customTpayClient")}
        help="Dla domyślnego konta zostaw puste pole"
      >
        <Select allowClear options={tpayOptions} placeholder={t<string>("products:form.customTpayClientPlaceholder")} />
      </Form.Item>

      <Form.Item
        name="customStripeAccount"
        tooltip={t<string>("products:form.customStripeClientTooltip")}
        label={t<string>("products:form.customStripeClient")}
        help="Dla domyślnego konta zostaw puste pole"
      >
        <Select
          allowClear
          options={stripeOptions}
          placeholder={t<string>("products:form.customStripeClientPlaceholder")}
        />
      </Form.Item>

      {gatewayChanged && hasReferences ? (
        <Alert
          type="warning"
          showIcon
          className="mt-2"
          message={t<string>("products:duplicate.gatewayMismatchTitle", "Zmiana konta płatności")}
          description={t<string>(
            "products:duplicate.gatewayMismatchDescription",
            "Produkt ma referencje do innych produktów (upsell / cross-sell / end-access). Po zmianie konta Stripe lub Tpay mogą one przestać być spójne z nowym kontem — przed publikacją zweryfikuj te powiązania.",
          )}
        />
      ) : null}
    </Form>
  );
};

export default DuplicateProductForm;
